<template>
  <div
    id="page-wrapper"
    class="wrap-container wrap-container--has-margin-bottom overview-page"
    role="main"
  >
    <on-page-breadcrumbs :data="breadcrumbs" />
    <preamble-block
      :title="title"
      :preamble="preamble"
      align="left"
    />

    <!-- CONVERSION BLOCKS -->
    <div
      v-if="conversionBlocks.length || (isEditable && conversionBlocks.length)"
      v-edit="'ConversionBlocks'"
      class="conversion-block__area"
    >
      <div class="row-1_lg-4_md-2">
        <conversion-block
          v-for="(item, index) in conversionBlocks"
          :key="'conversionBlock--' + item.url + index"
          :style="{ animationDelay: ( index * (1 / 10)) + 's' }"
          v-bind="item"
          component="ConversionBlock"
          class="fade-in-up col"
        />
      </div>
    </div>

    <!-- SIBLING/SUB PAGES - OVERVIEW NAVIGATION BLOCKS -->
    <div
      v-if="allNavigatablePages.length"
      class="overview-navigation-block__area"
    >
      <div :class="rowClass">
        <overview-navigation-block
          v-for="(item, index) in allNavigatablePages"
          :key="'overviewNavigationBlock--' + item.url + index"
          v-no-edit
          :style="{ animationDelay: ( index * (1 / 10)) + 's' }"
          :display-as-primary="displayFirstPuffAsPrimary && index === 0"
          v-bind="item"
          component="OverviewNavigationBlock"
          class="fade-in-up col"
        />
      </div>
    </div>

    <!-- EXTRA NAVIGATION BLOCKS, JUST FOR EPI EDIT -->
    <additional-navigation-blocks
      v-if="isEditable"
      :items="additionalNavigatablePages"
    />

    <!-- USP-BLOCK -->
    <div
      v-if="contentBlocks.length || (isEditable && contentBlocks.length)"
      v-edit="'ContentBlocks'"
      class="usp-block-wrapper fade-in"
      :style="{ animationDelay: ( (navigationBlocks.length + 2) * (1 / 8)) + 's' }"
    >
      <component
        :is="block.component | kebab"
        v-for="block in contentBlocks"
        :key="block.contentId"
        v-bind="block"
      />
    </div>

    <div
      v-if="accordionBlocks.length || (isEditable && accordionBlocks.length)"
      class="accordion-block-wrapper"
    >
      <sub-heading
        v-if="accordionsHeading || isEditable"
        :v-edit-heading="'AccordionsHeading'"
        :heading="accordionsHeading"
      />

      <in-view
        :threshold="0"
        :threshold-type="'px'"
        class="slide-into-view-transition"
      >
        <div
          v-edit="'AccordionBlocks'"
        >
          <accordion-group
            :items="accordionBlocks | stringify('content')"
            :limit="isEditable ? 0 : accordionShowMoreLimit"
            :tracking-scope="accordionsHeading"
          />
        </div>
      </in-view>
    </div>

    <navigation-blocks
      v-if="bottomNavigatablePages.length || isEditable"
      :navigation-blocks="bottomNavigatablePages"
    >
      <sub-heading
        v-if="bottomNavigationHeading || isEditable"
        :v-edit-heading="'BottomNavigationHeading'"
        :heading="bottomNavigationHeading"
      />
    </navigation-blocks>
  </div>
</template>
<script>
import pageWithNavigation from "@/vue/mixins/pageWithNavigation"
import PreambleBlock from '@/vue/blocks/PreambleBlock/preambleBlock'

export default {
  name: 'OverviewPage',
  components: {
    PreambleBlock
  },
  mixins: [pageWithNavigation],
  props: {
    title: {
      type: String,
      default: ""
    },
    preamble: {
      type: String,
      default: ""
    },
    breadcrumbs: {
      type: Array,
      default () {
        return []
      }
    },
    conversionBlocks: {
      type: Array,
      default () {
        return []
      }
    },
    contentBlocks: {
      type: Array,
      default () {
        return []
      }
    },
    navigationBlocks: {
      type: Array,
      default () {
        return []
      }
    },
    accordionsHeading: {
      type: String,
      default: ""
    },
    accordionShowMoreLimit: {
      type: Number,
      default: 0
    },
    accordionBlocks: {
      type: Array,
      default () {
        return []
      }
    },
    navigationHeading: {
      type: String,
      default: ""
    },
    displayFirstPuffAsPrimary: {
      type: Boolean,
      default: false
    },
    bottomNavigationHeading: {
      type: String,
      default: ""
    },
    bottomNavigatablePages: {
      type: Array,
      default () {
        return []
      }
    }
  },
  computed: {
    rowClass () {
      const nrOfNavigationBlocks = this.navigationBlocks.length
      if(nrOfNavigationBlocks <= 2){
        return "row-1_md-2"
      }
      const rowOfThreeHoles =
        nrOfNavigationBlocks % 3 === 0 ? 0 : 3 - (nrOfNavigationBlocks % 3)
      const rowOfFourHoles =
        nrOfNavigationBlocks % 4 === 0 ? 0 : 4 - (nrOfNavigationBlocks % 4)

      if (rowOfThreeHoles < rowOfFourHoles) {
        return "row-1_lg-3_md-2"
      }

      return "row-1_lg-4_md-2"
    }
  }
}
</script>

<style lang="scss">

.overview-page {
  .preamble {
    $text-max-width: 57.5rem;

    &__heading {
      margin: 1.5rem 0 1rem;
      max-width: $text-max-width;
      color: $color-brown-dark;
    }

    &__text {
      max-width: $text-max-width;
      margin: 1rem 0 0;
      color: $color-brown-dark;
    }
  }

  .accordion-block-wrapper {
    @include default-margin();
  }

  .overview-navigation-block__area,
  .conversion-block__area {
    @include default-margin-bottom();
  }
}
</style>
